<template>
  <HeaderBar />

  <section class="carvia-container space-y-4">
    <Input v-model="search" :placeholder="t('search')" class="w-full max-w-sm" variant="ghost" />

    <Tabs v-model="activeTab" :tabs="tabs" />

    <div class="flex flex-col gap-2">
      <PaginatedList
        :load="load"
        :query-key="[LEADS_QUERY_KEY, listQuery]"
        :empty-text="t('noLeads')"
      >
        <template #entry="{ entry: lead }">
          <LeadListItem :lead="lead" @select="handleSelect" />
        </template>
      </PaginatedList>
    </div>

    <LeadDetailModal v-if="leadId" :lead-id="leadId" @close="leadId = ''" />
  </section>
</template>

<script setup lang="ts">
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import Tabs, { type TabItem } from '@/components/Tabs.vue';
import Input from '@/components/Input.vue';
import PaginatedList from '@/components/PaginatedList.vue';
import LeadListItem from './LeadListItem.vue';
import LeadDetailModal from './LeadDetailModal.vue';
import { leadsService } from '@/api/leads.service';
import { LEADS_QUERY_KEY } from '@/queries/use-leads';
import { useRouteQueryRef } from '@/hooks/use-route-query-ref';
import { FindAllLeadsSort, LeadStatus, Order, type Lead } from '@carvia/ros-client-types';
import { computed, ref, watchEffect } from 'vue';
import { refDebounced } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useUiStore } from '@/stores/ui.store';
import { useQueryClient } from '@tanstack/vue-query';

enum Tab {
  NEW = 'NEW',
  CONTACTED = 'CONTACTED',
  COMPLETED = 'COMPLETED',
}

const { t } = useI18n();
const uiStore = useUiStore();
const queryClient = useQueryClient();
const activeTab = ref<Tab>(Tab.NEW);

const search = useRouteQueryRef('search', '', 'string');
const searchQuery = refDebounced(search, 500);
const leadId = useRouteQueryRef('leadId', '', 'string');

watchEffect(() => {
  uiStore.setHeaderTitle(t('websiteLeads'));
});

const tabs = computed<TabItem[]>(() => [
  { title: t('tabNew'), id: Tab.NEW },
  { title: t('tabContacted'), id: Tab.CONTACTED },
  { title: t('tabCompleted'), id: Tab.COMPLETED },
]);

const statusFilter = computed(() => {
  switch (activeTab.value) {
    case Tab.CONTACTED:
      return LeadStatus.CONTACTED;
    case Tab.COMPLETED:
      return LeadStatus.COMPLETED;
    default:
      return LeadStatus.NEW;
  }
});

const listQuery = computed(() => ({
  status: statusFilter.value,
  query: searchQuery.value || undefined,
  limit: 20,
  sort: [FindAllLeadsSort.CREATED_DATE],
  order: Order.DESC,
}));

const load = (page: number) =>
  leadsService.getAllWithQuery({
    ...listQuery.value,
    page,
  });

const handleSelect = (selected: Lead) => {
  queryClient.setQueryData([LEADS_QUERY_KEY, selected.id], selected);
  leadId.value = selected.id;
};
</script>

<i18n lang="json">
{
  "en": {
    "websiteLeads": "Website-Leads",
    "search": "Search",
    "tabNew": "New leads",
    "tabContacted": "Contacted leads",
    "tabCompleted": "Completed leads",
    "noLeads": "No leads found"
  },
  "de": {
    "websiteLeads": "Website-Leads",
    "search": "Suche",
    "tabNew": "Neue Leads",
    "tabContacted": "Kontaktierte Leads",
    "tabCompleted": "Abgeschlossene Leads",
    "noLeads": "Keine Leads gefunden"
  },
  "es": {
    "websiteLeads": "Leads web",
    "search": "Buscar",
    "tabNew": "Leads nuevos",
    "tabContacted": "Leads contactados",
    "tabCompleted": "Leads cerrados",
    "noLeads": "No hay leads"
  }
}
</i18n>
